// Error copy — what a Focus surface shows for a server `error` event, keyed on
// its `code` (`USER_ERROR_CODES` in `@loom/shared`). The wire `message` is never
// rendered there: it is static English for surfaces without a catalog. A code
// this catalog does not know shows `error.generic` (`focus/errorCopy.ts`).
//
// `error.apply.*` is the refine apply-fail screen's line for a failed
// `readiness_apply_result`, keyed on its code (`APPLY_ERROR_CODES`); a code it
// does not know shows `error.apply.apply_failed`.
//
// Plain words, no mechanism names, and every line says what the user can do.

import { PUBLIC_PRODUCT_NAME } from "@loom/shared/brand";

import type { MsgSection } from "./types.js";

export const errors: MsgSection = {
  "error.generic": {
    en: "Something went wrong on Loom's side. Please try again.",
    de: "Bei Loom ist etwas schiefgelaufen. Versuch es bitte nochmal.",
  },
  "error.request_failed": {
    en: "That didn't go through. Please try again.",
    de: "Das hat nicht geklappt. Versuch es bitte nochmal.",
  },
  "error.frame_invalid": {
    en: `${PUBLIC_PRODUCT_NAME} couldn't read that. Reload the page and try again.`,
    de: `${PUBLIC_PRODUCT_NAME} konnte das nicht lesen. Lade die Seite neu und versuch es nochmal.`,
  },
  "error.runtime_error": {
    en: `Something went wrong on ${PUBLIC_PRODUCT_NAME}'s side. Start over to carry on.`,
    de: `Bei ${PUBLIC_PRODUCT_NAME} ist etwas schiefgelaufen. Starte neu, um weiterzumachen.`,
  },
  "error.sandbox_not_ready": {
    en: `${PUBLIC_PRODUCT_NAME} isn't ready yet. Try again in a moment.`,
    de: `${PUBLIC_PRODUCT_NAME} ist noch nicht bereit. Versuch es gleich nochmal.`,
  },
  "error.budget_not_exhausted": {
    en: `There's nothing to extend right now. ${PUBLIC_PRODUCT_NAME} can keep working.`,
    de: `Im Moment gibt es nichts zu verlängern. ${PUBLIC_PRODUCT_NAME} kann weiterarbeiten.`,
  },
  "error.figma_preview_invalid": {
    en: `${PUBLIC_PRODUCT_NAME} couldn't take your answer about the design. Please try again.`,
    de: `${PUBLIC_PRODUCT_NAME} konnte deine Antwort zum Design nicht übernehmen. Versuch es bitte nochmal.`,
  },
  "error.field_not_declinable": {
    en: "This question can't be skipped.",
    de: "Diese Frage kannst du nicht überspringen.",
  },
  "error.edit_unavailable": {
    en: "Editing isn't available here yet. Write the change in the chat instead.",
    de: "Bearbeiten geht hier noch nicht. Schreib die Änderung stattdessen in den Chat.",
  },
  "error.edit_invalid": {
    en: `${PUBLIC_PRODUCT_NAME} couldn't take that change. Please try again.`,
    de: `${PUBLIC_PRODUCT_NAME} konnte diese Änderung nicht übernehmen. Versuch es bitte nochmal.`,
  },
  "error.edit_after_apply": {
    en: "This ticket is already saved. Reopen it to change it again.",
    de: "Dieses Ticket ist schon gespeichert. Öffne es nochmal, um es zu ändern.",
  },
  "error.edit_agent_unavailable": {
    en: `Your change is saved. ${PUBLIC_PRODUCT_NAME} works it in as soon as it's back.`,
    de: `Deine Änderung ist gespeichert und wird eingearbeitet, sobald ${PUBLIC_PRODUCT_NAME} wieder da ist.`,
  },
  "error.edit_failed": {
    en: `${PUBLIC_PRODUCT_NAME} couldn't save that change. Please try again.`,
    de: `${PUBLIC_PRODUCT_NAME} konnte diese Änderung nicht speichern. Versuch es bitte nochmal.`,
  },
  "error.answer_route_failed": {
    en: `${PUBLIC_PRODUCT_NAME} couldn't pass your answer on just now. Try again in a moment.`,
    de: `${PUBLIC_PRODUCT_NAME} konnte deine Antwort gerade nicht weitergeben. Versuch es gleich nochmal.`,
  },
  "error.servant_turn_failed": {
    en: `${PUBLIC_PRODUCT_NAME} couldn't answer that just now. Please send it again.`,
    de: `${PUBLIC_PRODUCT_NAME} konnte darauf gerade nicht antworten. Schick es bitte nochmal.`,
  },
  "error.servant_turn_timeout": {
    en: `${PUBLIC_PRODUCT_NAME}'s answer took too long and was stopped. Your message is kept and answered once ${PUBLIC_PRODUCT_NAME} is ready.`,
    de: `Die Antwort von ${PUBLIC_PRODUCT_NAME} hat zu lange gedauert und wurde abgebrochen. Deine Nachricht bleibt und wird beantwortet, sobald ${PUBLIC_PRODUCT_NAME} bereit ist.`,
  },
  "error.model_key_rejected": {
    en: "The API key was refused.",
    de: "Der API-Schlüssel wurde abgelehnt.",
  },
  "error.sandbox_terminated": {
    en: `This conversation isn't running any more. Restart ${PUBLIC_PRODUCT_NAME} to carry on.`,
    de: `Dieses Gespräch läuft nicht mehr. Starte ${PUBLIC_PRODUCT_NAME} neu, um weiterzumachen.`,
  },
  "error.assistant_not_configured": {
    en: `The chat assistant isn't set up for this project yet. Please ask whoever looks after ${PUBLIC_PRODUCT_NAME} for you.`,
    de: `Der Chat-Assistent ist für dieses Projekt noch nicht eingerichtet. Wende dich bitte an die Person, die ${PUBLIC_PRODUCT_NAME} bei euch betreut.`,
  },

  // ── Meetings (`MEETING_USER_ERROR_CODES`) ─────────────────────────────
  "error.not_eligible": {
    en: "Only the meeting's organizer can turn on recording.",
    de: "Nur die Organisatorin oder der Organisator kann die Aufnahme einschalten.",
  },
  "error.host_lost": {
    en: `${PUBLIC_PRODUCT_NAME} lost access to your Google account. Connect Google again.`,
    de: `${PUBLIC_PRODUCT_NAME} hat den Zugang zu deinem Google-Konto verloren. Verbinde Google neu.`,
  },
  "error.google_disconnected": {
    en: "Google was disconnected, so this meeting wasn't processed. Connect Google again.",
    de: "Google wurde getrennt, darum wurde dieses Meeting nicht verarbeitet. Verbinde Google neu.",
  },
  "error.public_repo": {
    en: `${PUBLIC_PRODUCT_NAME} files meeting tickets only into private projects.`,
    de: `${PUBLIC_PRODUCT_NAME} legt Tickets aus Meetings nur in privaten Projekten an.`,
  },
  "error.no_token": {
    en: `Sign in again so ${PUBLIC_PRODUCT_NAME} can file this meeting's tickets.`,
    de: `Melde dich nochmal an, damit ${PUBLIC_PRODUCT_NAME} die Tickets aus diesem Meeting anlegen kann.`,
  },
  "error.retarget_blocked": {
    en: `This ticket is now in another project, so ${PUBLIC_PRODUCT_NAME} didn't change it.`,
    de: `Dieses Ticket liegt jetzt in einem anderen Projekt, darum hat ${PUBLIC_PRODUCT_NAME} es nicht geändert.`,
  },
  "error.content_expired": {
    en: "The meeting's transcript was deleted after 30 days, so this isn't possible any more.",
    de: "Das Transkript des Meetings wurde nach 30 Tagen gelöscht, darum geht das nicht mehr.",
  },
  "error.meeting_too_long": {
    en: `This meeting is too long for ${PUBLIC_PRODUCT_NAME} to sort into tickets.`,
    de: `Dieses Meeting ist zu lang, als dass ${PUBLIC_PRODUCT_NAME} es in Tickets aufteilen könnte.`,
  },

  // ── A failed apply (the refine apply-fail screen) ──────────────────────
  "error.apply.apply_failed": {
    en: "Loom couldn't save the ticket. Please try again.",
    de: "Loom konnte das Ticket nicht speichern. Versuch es bitte nochmal.",
  },
  "error.apply.edit_pending": {
    en: "Your change isn't in the ticket yet. Wait a moment or keep the previous value, then accept again.",
    de: "Deine Änderung ist noch nicht im Ticket. Warte kurz oder behalte den vorherigen Wert und übernimm es dann nochmal.",
  },
  // No Retry beside this one (`applyRetryHelps`): the conversation has
  // written the ticket once, and every retry in it is refused the same way.
  "error.apply.card_already_applied": {
    en: "This ticket was already saved once in this conversation, so the newer version wasn't saved. Change it on the ticket's page, or refine it in a new conversation.",
    de: "Dieses Ticket wurde in diesem Gespräch schon einmal gespeichert, darum wurde die neuere Fassung nicht gespeichert. Ändere es auf der Seite des Tickets oder verfeinere es in einem neuen Gespräch.",
  },
  // Expired or moved, or the checked card is gone: the same token refuses the
  // same way, so the way out is a fresh check. The screen offers that in
  // Retry's place (`applyRetryHelps`).
  "error.apply.accept_token_unverified": {
    en: "The check on this ticket is out of date, so it wasn't saved. Refine it again, then accept.",
    de: "Die Prüfung dieses Tickets ist nicht mehr aktuell, darum wurde es nicht gespeichert. Verfeinere es nochmal und übernimm es dann.",
  },
  "error.apply.accept_token_required": {
    en: "Loom no longer has the checked version of this ticket. Refine it again, then accept.",
    de: "Loom hat die geprüfte Fassung dieses Tickets nicht mehr. Verfeinere es nochmal und übernimm es dann.",
  },
  "error.apply.card_lost": {
    en: "Loom no longer has the checked version of this ticket. Refine it again, then accept.",
    de: "Loom hat die geprüfte Fassung dieses Tickets nicht mehr. Verfeinere es nochmal und übernimm es dann.",
  },
  "error.apply.reconstruct_failed": {
    en: "Loom couldn't put the ticket text together, so nothing was saved. Refine it again, then accept.",
    de: "Loom konnte den Text des Tickets nicht zusammenstellen, darum wurde nichts gespeichert. Verfeinere es nochmal und übernimm es dann.",
  },
  "error.apply.access_denied": {
    en: "Loom couldn't confirm that you may change tickets in this project. Try again, or ask whoever looks after Loom for you.",
    de: "Loom konnte nicht bestätigen, dass du in diesem Projekt Tickets ändern darfst. Versuch es nochmal oder wende dich an die Person, die Loom bei euch betreut.",
  },
};
